<!--
  File name: FileInputUpload.vue
  Program Description:
    - This component customizes the default file input field to accommodate
    specific needs for uploading files including file size limits, multiple
    files, labels, and icons.

-->

<template>
  <v-container flex>
    <v-row>
      <v-col>
        <v-file-input
          :id="fileId"
          :class="inputClass"
          :show-size="showSize"
          :label="label"
          :placeholder="placeholder"
          :prepend-icon="prependIcon"
          :append-icon="appendIcon"
          :error-messages="errorMessages"
          :dense="dense"
          :value="currFiles"
          :disabled="!canEdit"
          :outlined="outlined"
          :accept="accept"
          :multiple="multiple"
          :clearable="clearable"
          :hint="hint"
          :hide-input="hideInput"
          :rules="rules"
          persistent-hint
          @change="merge"
          @click:clear="clearFiles"
        >
          <template v-slot:selection="{ index, text, file }">
            <v-chip
              :close="canEdit"
              small
              :color="chipColor"
              :style="chipStyle"
              @click:close="merge(null, index)"
            >
              {{ text || file.originalname }}
            </v-chip>
          </template>
        </v-file-input>
      </v-col>
      <v-col
        v-if="value && value.length > 0 && showDownload"
        cols="1"
        class="d-flex justify-end"
      >
        <v-btn
          class="2"
          fab
          dark
          small
          color="primary"
          @click="download(value)"
        >
          <v-icon dark>
            mdi-download
          </v-icon>
        </v-btn>
        <v-btn
          v-if="fileViewer.allow"
          class="ml-1"
          fab
          dark
          small
          color="primary"
          @click="createBlobURL(value)"
        >
          <v-icon dark>
            mdi-eye
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-dialog
      v-model="fileViewer.show"
      :fullscreen="fileViewer.maximize"
      persistent
    >
      <v-card min-height="700">
        <v-toolbar
          dark
          color="primary"
        >
          <v-toolbar-title>{{ label }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-title>{{ fileViewer.fileName }}</v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            dark
            @click="fileViewer.maximize = !fileViewer.maximize"
          >
            <v-icon>{{ fileViewer.maximize ? 'mdi-window-restore' : 'mdi-window-maximize' }}</v-icon>
          </v-btn>
          <v-btn
            icon
            dark
            @click="fileViewer.show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-progress-linear
          v-show="fileViewer.loading"
          indeterminate
        />
        <iframe
          v-if="fileViewer.show && !fileViewer.loading"
          id="file-viewer"
          :style="`height:${fileViewer.maximize ? '100' : '80'}vh;`"
          :src="fileViewer.src + '#toolbar=0&navpanes=0'"
          frameborder="0"
          loading="lazy"
        />
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
/* eslint-disable vue/prop-name-casing */

import crud from '@/utils/rest/crud'
export default {
  name: 'FileInput',
  model: {
    prop: 'val',
    event: 'change'
  },
  props: {
    inputClass: {
      type: String,
      default: undefined
    },
    showDownload: {
      type: Boolean,
      default: true
    },
    operation: {
      type: String,
      default: undefined
    },
    onTabView: Boolean,
    label: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    prependIcon: {
      type: String,
      default: undefined
    },
    appendIcon: {
      type: String,
      default: undefined
    },
    accept: {
      type: String,
      default: undefined
    },
    'error-messages': {
      type: Array,
      default: undefined
    },
    dense: Boolean,
    'show-size': Boolean,
    canEdit: Boolean,
    outlined: Boolean,
    // eslint-disable-next-line vue/require-default-prop
    value: {
      type: [Array, File],
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: undefined
    },
    rules: {
      type: Array,
      default: undefined
    },
    clearable: {
      type: Boolean,
      default: true
    },
    chips: {
      type: Boolean,
      default: false
    },
    'hide-input': {
      type: Boolean,
      default: false
    },
    fileId: {
      type: String,
      default: undefined
    },
    entryId: {
      type: String,
      default: undefined
    },
    fieldName: {
      type: String,
      default: undefined
    },
    collection: {
      type: String,
      default: undefined
    },
    'chip-color': {
      type: String,
      default: undefined
    },
    'chip-style': {
      type: Object,
      default: undefined
    },
    'is-store': {
      type: Boolean,
      default: false
    },
    canViewFile: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    currFiles: [],
    files: [],
    clear: false,
    fileViewer: {
      allow: false,
      show: false,
      fileName: '',
      src: '',
      maximize: false,
      loading: false
    }
  }),
  computed: {
    hint () {
      return `Can accept the following file types: ${this.$props.accept}`
    }
  },
  watch: {
    '$props.value' () {
      this.currFiles = this.$props.value
    },
    'fileViewer.show' (val) {
      if (!val) {
        window.URL.revokeObjectURL(this.fileViewer.src)
        this.fileViewer.src = ''
        this.fileViewer.maximize = false
      }
    }
  },
  beforeDestroy () {
    if (this.fileViewer.src) {
      window.URL.revokeObjectURL(this.fileViewer.src)
    }
  },
  created () {
    // if (this.onTabView) {
    //   this.currFiles = this.$props.value
    // }
    this.currFiles = this.$props.value
    if (this.currFiles && this.currFiles.length > 0) {
      const file = this.currFiles[0]
      const isImage = file.mimetype.includes('image')
      const isPDF = file.mimetype === 'application/pdf'
      this.fileViewer.allow = isImage || isPDF
    }
  },
  methods: {
    merge (e, index) {
      if (index || index === 0) this.deleteChip(index)

      if (!this.multiple) {
        this.$emit('change', e)
      } else {
        const names = new Set(this.currFiles.map(i => i.name))
        e && (this.$data.currFiles = [...this.$data.currFiles, ...e.filter(i => !names.has(i.name))])
        this.$emit('change', this.$data.currFiles)
      }
    },
    deleteChip (index) {
      if (this.isStore) {
        this.$emit('update-value', index)
      } else {
        const newValue = this.value
        if (Array.isArray(newValue)) {
          newValue.splice(index, 1)
          this.currFiles = newValue
          this.$emit('change', newValue)
        } else {
          this.currFiles = null
          this.$emit('change', null)
        }
      }
    },
    async download (files) {
      files.forEach(async (file) => {
        const resp = await crud(`attachment/${this.collection}/${this.entryId}/${this.fieldName}/${file.filename}`).get()
        if (resp.ok) {
          const data = await resp.json()
          const downloadLink = document.createElement('a')
          downloadLink.href = data.file
          downloadLink.download = file.originalname
          downloadLink.click()
        } else {
          this.localDownload(file)
        }
      })
    },
    async localDownload (file) {
      if (Array.isArray(file)) {
        file.forEach((item) => {
          this.downloadFile(item, item.name)
        })
      } else {
        this.downloadFile(file, file.name)
      }
    },
    downloadFile (blob, name) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.style.display = 'none'
      link.href = url
      link.download = name
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(url)
      link.parentNode.removeChild(link)
    },
    clearFiles () {
      this.currFiles = []
    },
    async createBlobURL (files) {
      // single pdf file viewer
      if (files.length !== 1) return
      const fv = this.fileViewer
      fv.show = true
      fv.loading = true
      const file = files[0]
      const route = `attachment/${this.collection}/${this.entryId}/${this.fieldName}/${file.filename}`
      const resp = await crud(route).get()
      const data = await resp.json()
      const base64Response = await fetch(data.file)
      const blob = await base64Response.blob()
      fv.fileName = file.filename
      fv.src = window.URL.createObjectURL(blob)
      fv.loading = false
    }
  }
}
</script>

<style scoped>
#file-viewer {
  display: block;
  width: 100vw;
  max-width: 100%;
}
</style>
